import React from 'react';
import { XMarkIcon, PencilIcon, TrashIcon, MapPinIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';

interface FieldDiaryDetailProps {
    entry: any;
    isOpen: boolean;
    onClose: () => void;
    onEdit: () => void;
    onDelete: () => void;
    canEdit: boolean;
}

const HEALTH_STATUS_CONFIG: Record<string, { bg: string; text: string; border: string }> = {
    excellent: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    good: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
    fair: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
    poor: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

const VISIT_TYPE_LABELS: Record<string, string> = {
    routine: 'Routine Visit',
    followup: 'Follow-up Visit',
    emergency: 'Emergency Visit',
    training: 'Training Visit',
};

const FieldDiaryDetail: React.FC<FieldDiaryDetailProps> = ({
    entry,
    isOpen,
    onClose,
    onEdit,
    onDelete,
    canEdit
}) => {
    if (!isOpen || !entry) return null;

    const healthConfig = HEALTH_STATUS_CONFIG[entry.health_status] || HEALTH_STATUS_CONFIG.good;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className={`px-6 py-4 ${healthConfig.bg} border-b ${healthConfig.border} flex items-center justify-between`}>
                    <div>
                        <h2 className={`text-xl font-bold ${healthConfig.text}`}>
                            {entry.cohort_name || 'Field Visit'}
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            {VISIT_TYPE_LABELS[entry.visit_type] || entry.visit_type}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canEdit && (
                            <>
                                <button
                                    onClick={onEdit}
                                    className="p-2 text-gray-600 hover:text-emerald-600 hover:bg-white/50 rounded-lg transition-colors"
                                >
                                    <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={onDelete}
                                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-white/50 rounded-lg transition-colors"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </>
                        )}
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white/50 rounded-lg transition-colors"
                        >
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Visit Info */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Date</p>
                                <p className="text-sm font-medium text-gray-900">
                                    {new Date(entry.visit_date).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-5 h-5 text-gray-400 flex items-center justify-center">👤</div>
                            <div>
                                <p className="text-xs text-gray-500">Scout</p>
                                <p className="text-sm font-medium text-gray-900">{entry.scout_name}</p>
                            </div>
                        </div>
                        {(entry.field_name || entry.field_code) && (
                            <div className="flex items-center gap-2">
                                <MapPinIcon className="w-5 h-5 text-gray-400" />
                                <div>
                                    <p className="text-xs text-gray-500">Field</p>
                                    <p className="text-sm font-medium text-gray-900">
                                        {entry.field_name || entry.field_code}
                                        {entry.field_code && entry.field_name ? ` (${entry.field_code})` : ''}
                                    </p>
                                    {entry.farmer_name && (
                                        <p className="text-xs text-gray-500">{entry.farmer_name}</p>
                                    )}
                                </div>
                            </div>
                        )}
                        {entry.location_name && (
                            <div className="flex items-center gap-2">
                                <MapPinIcon className="w-5 h-5 text-gray-400" />
                                <div>
                                    <p className="text-xs text-gray-500">Location</p>
                                    <p className="text-sm font-medium text-gray-900">{entry.location_name}</p>
                                </div>
                            </div>
                        )}
                        {entry.weather_condition && (
                            <div className="flex items-center gap-2">
                                <div className="w-5 h-5 text-gray-400 flex items-center justify-center">
                                    {entry.weather_condition === 'sunny' ? '☀️' :
                                     entry.weather_condition === 'cloudy' ? '☁️' :
                                     entry.weather_condition === 'rainy' ? '🌧️' : '🌤️'}
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500">Weather</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                        {entry.weather_condition}
                                        {entry.temperature_celsius && ` ${entry.temperature_celsius}°C`}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Health Status */}
                    <div className={`${healthConfig.bg} rounded-xl p-4 border ${healthConfig.border}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-700">Crop Health</p>
                                <p className={`text-2xl font-bold ${healthConfig.text} capitalize`}>
                                    {entry.health_status}
                                </p>
                            </div>
                            {entry.health_score && (
                                <div className="text-right">
                                    <p className="text-sm text-gray-600">Score</p>
                                    <p className={`text-3xl font-bold ${healthConfig.text}`}>
                                        {entry.health_score}
                                    </p>
                                </div>
                            )}
                        </div>
                        {entry.crop_type && (
                            <p className="mt-2 text-sm text-gray-600">
                                Crop: <span className="font-medium">{entry.crop_type}</span>
                                {entry.growth_stage && ` • Stage: ${entry.growth_stage}`}
                            </p>
                        )}
                    </div>

                    {/* Pest & Disease Alerts */}
                    {(entry.pest_observed || entry.disease_observed) && (
                        <div className="grid grid-cols-2 gap-4">
                            {entry.pest_observed && (
                                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                                    <p className="text-sm font-medium text-amber-700">🐛 Pest Observed</p>
                                    <p className="text-lg font-bold text-amber-900 mt-1">
                                        {entry.pest_type || 'Unknown pest'}
                                    </p>
                                    {entry.severity_level && (
                                        <p className="text-xs text-amber-600 mt-1">
                                            Severity: {entry.severity_level}
                                        </p>
                                    )}
                                </div>
                            )}
                            {entry.disease_observed && (
                                <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                                    <p className="text-sm font-medium text-red-700">🦠 Disease Observed</p>
                                    <p className="text-lg font-bold text-red-900 mt-1">
                                        {entry.disease_type || 'Unknown disease'}
                                    </p>
                                    {entry.severity_level && (
                                        <p className="text-xs text-red-600 mt-1">
                                            Severity: {entry.severity_level}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Activities */}
                    {entry.activities && entry.activities.length > 0 && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Activities Performed</p>
                            <div className="flex flex-wrap gap-2">
                                {entry.activities.map((activity: string, idx: number) => (
                                    <span
                                        key={idx}
                                        className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
                                    >
                                        {activity}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Notes */}
                    {entry.observations && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Observations</p>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.observations}</p>
                            </div>
                        </div>
                    )}

                    {entry.recommendations && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Recommendations</p>
                            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                <p className="text-sm text-blue-700 whitespace-pre-wrap">{entry.recommendations}</p>
                            </div>
                        </div>
                    )}

                    {entry.action_items && (
                        <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Action Items</p>
                            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                                <p className="text-sm text-purple-700 whitespace-pre-wrap">{entry.action_items}</p>
                            </div>
                        </div>
                    )}

                    {/* Follow-up */}
                    {entry.follow_up_required && (
                        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">📅</span>
                                <div>
                                    <p className="text-sm font-medium text-yellow-700">Follow-up Required</p>
                                    {entry.follow_up_date && (
                                        <p className="text-sm text-yellow-600">
                                            Due: {new Date(entry.follow_up_date).toLocaleDateString()}
                                        </p>
                                    )}
                                    {entry.follow_up_notes && (
                                        <p className="text-sm text-yellow-600 mt-1">{entry.follow_up_notes}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Status & Timestamps */}
                    <div className="flex items-center justify-between text-xs text-gray-500 pt-4 border-t border-gray-100">
                        <span>Status: <span className="font-medium capitalize">{entry.status}</span></span>
                        <span>Created: {new Date(entry.created_at).toLocaleString()}</span>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FieldDiaryDetail;
